import { useState, useMemo } from 'react'
import { useRecipes } from './hooks/useRecipes'
import { RecipeCard } from './components/RecipeCard'
import { RecipeDetail } from './components/RecipeDetail'
import { RecipeForm } from './components/RecipeForm'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState }  from '../../components/ui/EmptyState'
import { cn } from '../../lib/utils'

const SORT_OPTS = [
  { id: 'newest',   label: 'Newest' },
  { id: 'rating',   label: 'Top Rated' },
  { id: 'quickest', label: 'Quickest' },
]

export const RecipesPage = () => {
  const { data: recipes = [], isLoading } = useRecipes()
  const [search,       setSearch]       = useState('')
  const [sortBy,       setSortBy]       = useState('newest')
  const [minRating,    setMinRating]    = useState(0)
  const [maxCookTime,  setMaxCookTime]  = useState('')
  const [activeRecipe, setActiveRecipe] = useState(null)
  const [editRecipe,   setEditRecipe]   = useState(null)
  const [showFilters,  setShowFilters]  = useState(false)

  const filtered = useMemo(() => {
    let list = recipes
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((r) => r.title.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q))
    }
    if (minRating > 0) list = list.filter((r) => (r.rating ?? 0) >= minRating)
    if (maxCookTime)   list = list.filter((r) => r.cook_time_minutes && r.cook_time_minutes <= parseInt(maxCookTime))

    if (sortBy === 'rating')   list = [...list].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
    if (sortBy === 'quickest') list = [...list].sort((a, b) => (a.cook_time_minutes ?? 999) - (b.cook_time_minutes ?? 999))
    return list
  }, [recipes, search, sortBy, minRating, maxCookTime])

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Recipes</h1>
          <p className="text-sm text-text-muted mt-0.5">{filtered.length} recipe{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => setEditRecipe('new')}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New
        </button>
      </div>

      {/* Search + filters */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Search recipes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-xl border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={cn('px-3 py-2 rounded-xl border text-sm transition-colors', showFilters ? 'border-primary text-primary bg-primary-light' : 'border-border text-text-muted hover:border-primary/50')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
          </svg>
        </button>
      </div>

      {showFilters && (
        <div className="bg-background rounded-xl p-4 mb-4 space-y-3">
          <div className="flex gap-1">
            {SORT_OPTS.map((o) => (
              <button
                key={o.id}
                onClick={() => setSortBy(o.id)}
                className={cn(
                  'flex-1 py-1.5 text-xs font-medium rounded-lg transition-colors',
                  sortBy === o.id ? 'bg-primary text-white' : 'text-text-muted hover:bg-surface',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Min Rating</label>
              <div className="flex gap-1">
                {[0, 1, 2, 3, 4, 5].map((r) => (
                  <button key={r} onClick={() => setMinRating(r)}
                    className={cn('flex-1 text-xs py-1 rounded', minRating === r ? 'bg-primary text-white' : 'bg-surface border border-border text-text-muted')}>
                    {r === 0 ? 'All' : r + '★'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Max Cook Time</label>
              <select
                value={maxCookTime}
                onChange={(e) => setMaxCookTime(e.target.value)}
                className="w-full rounded-lg border border-border px-2 py-1.5 text-sm"
              >
                <option value="">Any</option>
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 gap-3">
          <SkeletonList count={4} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          type="recipes"
          title={recipes.length === 0 ? 'No recipes yet' : 'No recipes match your filters'}
          description={recipes.length === 0 ? 'Tap New Recipe to add your first dish.' : 'Try a different search or tag filter.'}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} onClick={setActiveRecipe} />
          ))}
        </div>
      )}

      {activeRecipe && !editRecipe && (
        <RecipeDetail
          recipe={activeRecipe}
          onEdit={(r) => { setEditRecipe(r); setActiveRecipe(null) }}
          onClose={() => setActiveRecipe(null)}
        />
      )}

      {editRecipe && (
        <RecipeForm
          recipe={editRecipe === 'new' ? null : editRecipe}
          onClose={() => setEditRecipe(null)}
        />
      )}
    </div>
  )
}
